import React from "react";
import { motion } from "framer-motion";
import { 
  ArrowLeft, Shield, Lock,  Database, 
   Globe, Trash2, Mail, Info 
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1, 
      transition: { duration: 0.5, staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="min-h-screen bg-white p-6 md:p-12 pb-24"
    >
      <div className="max-w-3xl mx-auto">
        {/* BACK BUTTON */}
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-emerald-600 font-black uppercase text-xs tracking-[0.2em] mb-10 hover:gap-4 transition-all"
        >
          <ArrowLeft size={18} /> Back
        </button>

        {/* HEADER */}
        <header className="mb-16">
          <h1 className="text-5xl font-black text-gray-900 mb-4 tracking-tighter italic">
            Privacy Policy
          </h1>
          <div className="flex items-center gap-2 text-gray-400 font-bold text-xs uppercase tracking-[0.3em]">
            <span>Version 2.1</span>
            <span className="h-1 w-1 bg-gray-300 rounded-full"></span>
            <span>Effective Jan 2026</span>
          </div>
        </header>

        {/* CONTENT SECTIONS */}
        <div className="space-y-16">
          
          <motion.section variants={itemVariants} className="border-l-4 border-emerald-500 pl-6">
            <h2 className="font-black text-gray-900 uppercase tracking-widest text-sm mb-4 flex items-center gap-3">
                <Shield size={18} className="text-emerald-500" /> 01. Data Collection
            </h2>
            <div className="text-gray-600 font-medium leading-relaxed space-y-4">
              <p>We collect information to provide better services to all our users. This includes:</p>
              <ul className="list-disc ml-5 space-y-2">
                <li><span className="text-gray-900 font-bold">Profile Data:</span> Name, email address, and age.</li>
                <li><span className="text-gray-900 font-bold">Health Metrics:</span> Weight, height, target goals, and body fat percentage.</li>
                <li><span className="text-gray-900 font-bold">Activity Logs:</span> Workout frequency, duration, and exercise types.</li>
              </ul>
            </div>
          </motion.section>

          <motion.section variants={itemVariants} className="border-l-4 border-emerald-500 pl-6">
            <h2 className="font-black text-gray-900 uppercase tracking-widest text-sm mb-4 flex items-center gap-3">
                <Database size={18} className="text-emerald-500" /> 02. How We Use Data
            </h2>
            <p className="text-gray-600 font-medium leading-relaxed">
              The information we collect is used to calculate your BMI, estimate caloric needs, and track progress against your goals. We use your email to send system notifications, security alerts, and, if opted-in, personalized fitness tips. We do not engage in automated decision-making that significantly affects you.
            </p>
          </motion.section>

          <motion.section variants={itemVariants} className="border-l-4 border-emerald-500 pl-6">
            <h2 className="font-black text-gray-900 uppercase tracking-widest text-sm mb-4 flex items-center gap-3">
                <Globe size={18} className="text-emerald-500" /> 03. Third-Party Sharing
            </h2>
            <p className="text-gray-600 font-medium leading-relaxed">
              We do not sell, trade, or otherwise transfer your health data to outside parties. This does not include trusted partners who assist us in operating our app (like <span className="text-emerald-600">Supabase</span> for database security), as long as those parties agree to keep this information confidential.
            </p>
          </motion.section>

          <motion.section variants={itemVariants} className="border-l-4 border-emerald-500 pl-6">
            <h2 className="font-black text-gray-900 uppercase tracking-widest text-sm mb-4 flex items-center gap-3">
                <Lock size={18} className="text-emerald-500" /> 04. Data Security
            </h2>
            <p className="text-gray-600 font-medium leading-relaxed">
              We implement a variety of security measures to maintain the safety of your personal information. Your data is contained behind secured networks and is only accessible by a limited number of persons who have special access rights to such systems.
            </p>
          </motion.section>

          <motion.section variants={itemVariants} className="border-l-4 border-emerald-500 pl-6">
            <h2 className="font-black text-gray-900 uppercase tracking-widest text-sm mb-4 flex items-center gap-3">
                <Trash2 size={18} className="text-emerald-500" /> 05. Data Retention & Deletion
            </h2>
            <p className="text-gray-600 font-medium leading-relaxed">
              We retain your information for as long as your account is active. You may request account deletion at any time. Upon deletion, all health-related metrics and personal identifiers are purged from our primary database within 30 days.
            </p>
          </motion.section>

         

          <motion.section variants={itemVariants} className="border-l-4 border-emerald-500 pl-6">
            <h2 className="font-black text-gray-900 uppercase tracking-widest text-sm mb-4 flex items-center gap-3">
                <Mail size={18} className="text-emerald-500" /> 06. Contact Us
            </h2>
            <p className="text-gray-600 font-medium leading-relaxed">
              If there are any questions regarding this privacy policy, you may contact us using the information below:
              <br /><br />
              <span className="block text-gray-900 font-black">Privacy Department</span>
              <span className="block">support@fitnesstracker.in</span>
             
            </p>
          </motion.section>

        </div>

        {/* FOOTER NOTE */}
        <footer className="mt-24 pt-10 border-t border-gray-100 text-center">
          <p className="text-gray-400 text-sm font-medium italic">
            Your trust is our most important metric. Thank you for choosing Fitness Pro.
          </p>
        </footer>
      </div>
    </motion.div>
  );
};

export default PrivacyPolicy;